/**
 * Starter templates for websites without a shop (simple-site §7): "Wizytówka usługowa" and
 * "Szkoła". Each template is a list of Studio documents plus per-document starting blocks that
 * replace the shop defaults. No shop blocks; every size has a mobile override.
 */
import { buildAccessibilityStatementBlocks } from '@/core/legal/accessibility-statement';
import { globals } from './presets';

export type StudioTemplate = 'shop' | 'service' | 'school';

export const STUDIO_TEMPLATES: { id: StudioTemplate; title: string; description: string }[] = [
  { id: 'shop', title: 'Sklep internetowy', description: 'Pełny sklep: produkty, koszyk, zamówienie, konto klienta.' },
  { id: 'service', title: 'Wizytówka usługowa', description: 'Strona główna, O nas, Oferta, Galeria, Aktualności, Kontakt.' },
  { id: 'school', title: 'Szkoła', description: 'Aktualności, O szkole, Kadra, Dokumenty, Kalendarz, Kontakt, Deklaracja dostępności.' },
];

const INK = '#0f172a';
const MUTED = '#475569';
const SOFT_BG = '#f7f5f0';

type NavItem = { label: string; url: string };

const siteHeader = (items: NavItem[]) => [
  globals(),
  {
    id: 'site-header-row',
    type: 'row',
    customName: 'Logo · menu',
    settings: { columns: 2, columnWidths: [28, 72], mobileColumns: 2, mobileLayout: 'header-compact', alignItems: 'center' },
    styles: { backgroundColor: '#ffffff', color: INK, paddingTop: 14, paddingBottom: 14, mobile: { paddingTop: 10, paddingBottom: 10 } },
    children: [
      { id: 'site-header-logo-column', type: 'container', customName: 'Logo', settings: {}, styles: {}, children: [
        { id: 'site-header-logo', type: 'store-logo', customName: 'Logo', content: '', settings: { href: '/', heightDesktop: 48, heightMobile: 36, alignmentDesktop: 'left', alignmentMobile: 'left' }, styles: {} },
      ] },
      { id: 'site-header-nav-column', type: 'container', customName: 'Menu', settings: {}, styles: {}, children: [
        { id: 'site-header-nav', type: 'store-navigation', customName: 'Menu strony', content: '', settings: { source: 'custom', items, direction: 'horizontal', alignmentDesktop: 'right', alignmentMobile: 'right', mobileMode: 'drawer-right', showMobileLabel: true, mobileLabel: 'Menu' }, styles: {} },
      ] },
    ],
  },
];

const siteFooter = (about: string, links: NavItem[]) => [
  globals(),
  {
    id: 'site-footer-row',
    type: 'row',
    customName: 'Stopka',
    settings: { columns: 3, columnWidths: [40, 30, 30], mobileColumns: 1, alignItems: 'start' },
    styles: { backgroundColor: INK, color: '#ffffff', paddingTop: 56, paddingBottom: 40, mobile: { paddingTop: 40, paddingBottom: 28 } },
    children: [
      { id: 'site-footer-brand', type: 'container', customName: 'O nas', settings: {}, styles: {}, children: [
        { id: 'site-footer-logo', type: 'store-logo', content: '', settings: { href: '/', heightDesktop: 42, heightMobile: 36, alignmentDesktop: 'left' }, styles: { color: '#ffffff', marginBottom: 16 } },
        { id: 'site-footer-about', type: 'text', content: about, settings: {}, styles: { color: '#e2e8f0', fontSize: 14, lineHeight: 1.7 } },
      ] },
      { id: 'site-footer-links', type: 'container', customName: 'Na skróty', settings: {}, styles: {}, children: [
        { id: 'site-footer-links-title', type: 'header', content: 'Na skróty', settings: { level: 'h2' }, styles: { color: '#ffffff', fontSize: 18, marginBottom: 12 } },
        { id: 'site-footer-links-text', type: 'text', content: links.map((l) => `<p><a href="${l.url}">${l.label}</a></p>`).join(''), settings: {}, styles: { color: '#e2e8f0', fontSize: 14, lineHeight: 1.9 } },
      ] },
      { id: 'site-footer-contact', type: 'container', customName: 'Kontakt', settings: {}, styles: {}, children: [
        { id: 'site-footer-contact-title', type: 'header', content: 'Kontakt', settings: { level: 'h2' }, styles: { color: '#ffffff', fontSize: 18, marginBottom: 12 } },
        // Empty contact-hours fields fall back to Ustawienia → Dane firmy on the live site.
        { id: 'site-footer-contact-hours', type: 'contact-hours', content: '', settings: { title: '', show_hours: false, show_map: false }, styles: { color: '#e2e8f0' } },
      ] },
    ],
  },
];

const hero = (id: string, title: string, lead: string, cta?: NavItem) => ({
  id,
  type: 'container',
  customName: 'Wprowadzenie',
  settings: {},
  styles: { paddingTop: 88, paddingBottom: 80, backgroundColor: SOFT_BG, textAlign: 'center', mobile: { paddingTop: 48, paddingBottom: 44 } },
  children: [
    { id: `${id}-title`, type: 'header', content: title, settings: { level: 'h1' }, styles: { color: INK, fontSize: 52, fontWeight: 800, lineHeight: 1.1, textAlign: 'center', marginBottom: 16, mobile: { fontSize: 34, marginBottom: 12 } } },
    { id: `${id}-lead`, type: 'text', content: lead, settings: {}, styles: { color: MUTED, fontSize: 19, lineHeight: 1.65, textAlign: 'center', maxWidth: 760, marginBottom: cta ? 28 : 0, mobile: { fontSize: 16 } } },
    ...(cta ? [{ id: `${id}-cta`, type: 'button', content: cta.label, settings: { actionType: 'link', href: cta.url, target: '_self' }, styles: { backgroundColor: INK, color: '#ffffff', borderRadius: 10, textAlign: 'center' } }] : []),
  ],
});

const pageHero = (id: string, title: string, lead: string) => ({
  id,
  type: 'container',
  customName: 'Nagłówek strony',
  settings: {},
  styles: { paddingTop: 58, paddingBottom: 36, backgroundColor: SOFT_BG, mobile: { paddingTop: 36, paddingBottom: 24 } },
  children: [
    { id: `${id}-title`, type: 'header', content: title, settings: { level: 'h1' }, styles: { color: INK, fontSize: 42, fontWeight: 750, lineHeight: 1.15, marginBottom: 12, mobile: { fontSize: 30 } } },
    { id: `${id}-lead`, type: 'text', content: lead, settings: {}, styles: { color: MUTED, fontSize: 17, lineHeight: 1.7, maxWidth: 780, mobile: { fontSize: 15 } } },
  ],
});

const section = (id: string, child: any, background = '#ffffff') => ({
  id,
  type: 'container',
  customName: child.customName || 'Sekcja',
  settings: {},
  styles: { maxWidth: '1120px', paddingTop: 56, paddingBottom: 56, backgroundColor: background, mobile: { paddingTop: 32, paddingBottom: 32 } },
  children: [child],
});

const textBlock = (id: string, html: string) => ({ id, type: 'text', content: html, settings: {}, styles: { color: '#334155', fontSize: 16, lineHeight: 1.8, mobile: { fontSize: 15 } } });

const contactPage = (id: string, lead: string) => [
  globals(),
  pageHero(`${id}-hero`, 'Kontakt', lead),
  section(`${id}-hours`, { id: `${id}-hours-block`, type: 'contact-hours', customName: 'Kontakt i godziny', content: '', settings: { title: '' }, styles: { columns: 2, mapHeight: 320, mobile: { mapHeight: 240 } } }),
  section(`${id}-form`, { id: `${id}-form-block`, type: 'contact-form', customName: 'Formularz', content: '', settings: {}, styles: {} }, SOFT_BG),
];

const newsPage = (id: string, lead: string) => [
  globals(),
  pageHero(`${id}-hero`, 'Aktualności', lead),
  section(`${id}-posts`, { id: `${id}-posts-block`, type: 'blog-posts', customName: 'Wpisy', content: '', settings: { columns: 3, columns_mobile: 1 }, styles: {} }),
];

const aboutPage = (id: string, title: string, lead: string, html: string) => [
  globals(),
  pageHero(`${id}-hero`, title, lead),
  section(`${id}-body`, textBlock(`${id}-text`, html)),
];

// --- Wizytówka usługowa ---------------------------------------------------------------------

const SERVICE_NAV: NavItem[] = [
  { label: 'Strona główna', url: '/' }, { label: 'O nas', url: '/o-nas' }, { label: 'Oferta', url: '/uslugi' },
  { label: 'Galeria', url: '/galeria' }, { label: 'Aktualności', url: '/blog' }, { label: 'Kontakt', url: '/kontakt' },
];

const serviceCards = (id: string, cards: { iconName: string; title: string; description: string }[], columns = 3) => ({
  id, type: 'feature-cards', customName: 'Karty', content: '', settings: { columns, columnsMobile: 1, gap: 24, cards }, styles: {},
});

export const SERVICE_TEMPLATE: Record<string, any[]> = {
  header: siteHeader(SERVICE_NAV),
  footer: siteFooter('Krótko o firmie: czym się zajmujemy, dla kogo i na jakim obszarze działamy.', [
    { label: 'Oferta', url: '/uslugi' }, { label: 'Kontakt', url: '/kontakt' }, { label: 'Polityka prywatności', url: '/polityka-prywatnosci' },
  ]),
  home: [
    globals(),
    hero('svc-home-hero', 'Rzetelne usługi w Twojej okolicy', 'Jedno zdanie o tym, co robisz najlepiej i dlaczego warto zadzwonić właśnie do Ciebie.', { label: 'Umów się', url: '/kontakt' }),
    section('svc-home-usp', serviceCards('svc-home-usp-cards', [
      { iconName: 'Clock', title: 'Szybkie terminy', description: 'Wolne terminy zwykle w tym samym tygodniu.' },
      { iconName: 'ShieldCheck', title: 'Gwarancja', description: 'Na każdą usługę dajemy pisemną gwarancję.' },
      { iconName: 'MapPin', title: 'Dojazd do klienta', description: 'Działamy w mieście i okolicy do 30 km.' },
    ])),
    section('svc-home-quotes', { id: 'svc-home-testimonials', type: 'testimonials', customName: 'Opinie', content: '', settings: {}, styles: {} }, SOFT_BG),
    section('svc-home-contact', { id: 'svc-home-contact-hours', type: 'contact-hours', customName: 'Kontakt i godziny', content: '', settings: {}, styles: { columns: 2, mobile: { mapHeight: 220 } } }),
  ],
  about: aboutPage('svc-about', 'O nas', 'Poznaj ludzi, którzy stoją za firmą.', '<h2>Nasza historia</h2><p>Opisz, od kiedy działacie, czym się wyróżniacie i jakie macie doświadczenie.</p><h2>Jak pracujemy</h2><p>Krok po kroku: kontakt, wycena, realizacja, odbiór.</p>'),
  offer: [
    globals(),
    pageHero('svc-offer-hero', 'Oferta', 'Usługi, które wykonujemy — z orientacyjnymi cenami i czasem realizacji.'),
    section('svc-offer-cards', serviceCards('svc-offer-cards-block', [
      { iconName: 'Wrench', title: 'Usługa 1', description: 'Na czym polega, ile trwa, od ile kosztuje.' },
      { iconName: 'Sparkles', title: 'Usługa 2', description: 'Na czym polega, ile trwa, od ile kosztuje.' },
      { iconName: 'Hammer', title: 'Usługa 3', description: 'Na czym polega, ile trwa, od ile kosztuje.' },
    ])),
    section('svc-offer-faq', { id: 'svc-offer-faq-block', type: 'faq', customName: 'Pytania', settings: { title: 'Najczęstsze pytania', layout: 'accordion', questions: [] }, styles: {} }, SOFT_BG),
  ],
  gallery: [
    globals(),
    pageHero('svc-gallery-hero', 'Galeria', 'Wybrane realizacje. Dodaj zdjęcia z opisem (tekst alternatywny), żeby galeria była dostępna.'),
    section('svc-gallery', { id: 'svc-gallery-block', type: 'media-gallery', customName: 'Galeria', content: '', settings: { items: [], columns: 3, columnsMobile: 2 }, styles: {} }),
  ],
  blog: newsPage('svc-news', 'Nowości, realizacje i porady.'),
  contact: contactPage('svc-contact', 'Zadzwoń, napisz albo odwiedź nas — odpowiadamy w ciągu jednego dnia roboczego.'),
};

// --- Szkoła ---------------------------------------------------------------------------------

const SCHOOL_NAV: NavItem[] = [
  { label: 'Strona główna', url: '/' }, { label: 'Aktualności', url: '/blog' }, { label: 'O szkole', url: '/o-nas' },
  { label: 'Kadra', url: '/kadra' }, { label: 'Dokumenty', url: '/dokumenty' }, { label: 'Kalendarz', url: '/kalendarz' },
  { label: 'Kontakt', url: '/kontakt' },
];

export const SCHOOL_TEMPLATE: Record<string, any[]> = {
  header: siteHeader(SCHOOL_NAV),
  footer: siteFooter('Pełna nazwa szkoły, patron i krótka informacja dla rodziców.', [
    { label: 'Dokumenty', url: '/dokumenty' }, { label: 'Deklaracja dostępności', url: '/deklaracja-dostepnosci' },
    { label: 'Polityka prywatności', url: '/polityka-prywatnosci' },
  ]),
  home: [
    globals(),
    hero('sch-home-hero', 'Szkoła, w której każdy uczeń się liczy', 'Krótkie powitanie dyrekcji: czym żyje szkoła, co oferujemy uczniom i rodzicom.', { label: 'Rekrutacja', url: '/dokumenty' }),
    section('sch-home-calendar', { id: 'sch-home-calendar-block', type: 'school-calendar', customName: 'Najbliższe wydarzenia', content: '', settings: { view: 'list', title: 'Najbliższe wydarzenia', limit: 4 }, styles: {} }),
    section('sch-home-news', { id: 'sch-home-news-block', type: 'blog-posts', customName: 'Aktualności', content: '', settings: { columns: 3, columns_mobile: 1, limit: 3 }, styles: {} }, SOFT_BG),
    section('sch-home-contact', { id: 'sch-home-contact-hours', type: 'contact-hours', customName: 'Sekretariat', content: '', settings: { title: 'Sekretariat' }, styles: { columns: 2, mobile: { mapHeight: 220 } } }),
  ],
  blog: newsPage('sch-news', 'Co słychać w szkole: wydarzenia, sukcesy uczniów, ogłoszenia.'),
  about: aboutPage('sch-about', 'O szkole', 'Historia, patron, misja i to, co nas wyróżnia.', '<h2>Historia i patron</h2><p>Kilka akapitów o historii szkoły.</p><h2>Oferta edukacyjna</h2><p>Zajęcia dodatkowe, koła zainteresowań, świetlica, stołówka.</p>'),
  staff: [
    globals(),
    pageHero('sch-staff-hero', 'Kadra', 'Dyrekcja, nauczyciele i specjaliści — z godzinami dostępności dla rodziców.'),
    section('sch-staff', { id: 'sch-staff-block', type: 'staff-directory', customName: 'Kadra', content: '', settings: { title: '', columns: 3, people: [] }, styles: {} }),
  ],
  documents: [
    globals(),
    pageHero('sch-docs-hero', 'Dokumenty', 'Statut, regulaminy, rekrutacja i plany lekcji do pobrania.'),
    section('sch-docs', { id: 'sch-docs-block', type: 'download', customName: 'Dokumenty', content: '', settings: { mode: 'library', layout: 'tabs', groups: [
      { id: 'sch-docs-g1', title: 'Statut i regulaminy', items: [] },
      { id: 'sch-docs-g2', title: 'Rekrutacja', items: [] },
      { id: 'sch-docs-g3', title: 'Plany lekcji', items: [] },
    ] }, styles: {} }),
  ],
  calendar: [
    globals(),
    pageHero('sch-cal-hero', 'Kalendarz', 'Rok szkolny, egzaminy, zebrania i dni wolne. Możesz dodać go do swojego kalendarza w telefonie.'),
    section('sch-cal', { id: 'sch-cal-block', type: 'school-calendar', customName: 'Kalendarz szkolny', content: '', settings: { view: 'grid', title: '' }, styles: {} }),
  ],
  contact: contactPage('sch-contact', 'Sekretariat szkoły — godziny pracy, telefon, e-mail i dojazd.'),
  accessibility: [
    globals(),
    pageHero('sch-a11y-hero', 'Deklaracja dostępności', 'Uzupełnij pola w nawiasach [ ] przed publikacją.'),
    section('sch-a11y', buildAccessibilityStatementBlocks(null)[0]),
  ],
};

/** Site-only documents (not part of the shop template). Order = Studio dashboard order. */
export const SITE_DOCUMENTS = [
  { id: 'offer', title: 'Oferta', description: 'Usługi z opisem, cenami i pytaniami.', exportPath: 'pages/offer.json', slug: 'uslugi' },
  { id: 'gallery', title: 'Galeria', description: 'Realizacje w galerii z lightboxem.', exportPath: 'pages/gallery.json', slug: 'galeria' },
  { id: 'staff', title: 'Kadra', description: 'Dyrekcja, nauczyciele, specjaliści.', exportPath: 'pages/staff.json', slug: 'kadra' },
  { id: 'documents', title: 'Dokumenty', description: 'Repozytorium dokumentów w grupach.', exportPath: 'pages/documents.json', slug: 'dokumenty' },
  { id: 'calendar', title: 'Kalendarz', description: 'Kalendarz szkolny z subskrypcją .ics.', exportPath: 'pages/calendar.json', slug: 'kalendarz' },
  { id: 'accessibility', title: 'Deklaracja dostępności', description: 'Wymagana od szkół i innych podmiotów publicznych.', exportPath: 'pages/accessibility.json', slug: 'deklaracja-dostepnosci' },
] as const;

export const TEMPLATE_DOCUMENT_IDS: Record<Exclude<StudioTemplate, 'shop'>, string[]> = {
  service: ['header', 'footer', 'home', 'about', 'offer', 'gallery', 'blog', 'contact', 'privacy', 'cookies'],
  school: ['header', 'footer', 'home', 'blog', 'about', 'staff', 'documents', 'calendar', 'contact', 'accessibility', 'privacy', 'cookies'],
};

export const TEMPLATE_BLOCKS: Record<Exclude<StudioTemplate, 'shop'>, Record<string, any[]>> = {
  service: SERVICE_TEMPLATE,
  school: SCHOOL_TEMPLATE,
};

/** Titles that read differently on a website (blog → "Aktualności", about → "O szkole"). */
export const TEMPLATE_TITLES: Record<Exclude<StudioTemplate, 'shop'>, Record<string, string>> = {
  service: { blog: 'Aktualności', home: 'Strona główna' },
  school: { blog: 'Aktualności', about: 'O szkole' },
};

/** Generic starting blocks for the site documents when they are opened outside their template. */
export const SITE_DOCUMENT_BLOCKS: Record<string, any[]> = {
  offer: SERVICE_TEMPLATE.offer,
  gallery: SERVICE_TEMPLATE.gallery,
  staff: SCHOOL_TEMPLATE.staff,
  documents: SCHOOL_TEMPLATE.documents,
  calendar: SCHOOL_TEMPLATE.calendar,
  accessibility: SCHOOL_TEMPLATE.accessibility,
};
